import { Component, OnInit } from '@angular/core';
import { ValidationHandlerService, TranslatedToastrService, BaseComponent, LanguageService, ListItemModel } from 'lib/Vestra.shared';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService, CouponModel, CouponSaveModel, VestraService } from 'src/app/services/api-services';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'app-coupon-edit',
    templateUrl: './coupon-edit.component.html',
    styleUrls: ['./coupon-edit.component.scss'],
    standalone: false,
})
export class CouponEditComponent extends BaseComponent implements OnInit {

    public couponRedeemTypes: ListItemModel[] = [];


    get showDeleteButton(): boolean {
        return this.item && this.item?.id !== 0 && !this.item?.isDeleted;
    }

    get showUndeleteButton(): boolean {
        return this.item && this.item?.id !== 0 && this.item?.isDeleted;
    }

    constructor(
        protected override validationHandlerService: ValidationHandlerService,
        private translatedToastrService: TranslatedToastrService,
        private translateService: TranslateService,
        protected override modalService: BsModalService,
        private vestraService: VestraService,
        private authenticationService: AuthenticationService,
        protected override spinnerService: NgxSpinnerService,
        protected override languageService: LanguageService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        super(spinnerService, modalService, validationHandlerService, translateService, languageService);
    }

    //input properties
    public title: string;
    public item: CouponModel = null;
    public partnerId: number;
    public couponId: number;

    private files: { [key: string]: File | null } = {
        couponImage: null,
        couponGif: null,
    };

    public fileNames: { [key: string]: string } = {
        couponImage: '',
        couponGif: '',
    };

    public form: FormGroup = this.getFormGroup();

    private getFormGroup(): FormGroup {
        return new FormGroup({
            partnerId: new FormControl(0),
            title: new FormControl('', [Validators.required]),
            subtitle: new FormControl('', [Validators.required]),
            usageInstructions: new FormControl('', [Validators.required]),
            couponCode: new FormControl('', [Validators.required]),
            description: new FormControl(''),
            price: new FormControl(0, [Validators.required]),
            discountPercentage: new FormControl(null, [Validators.max(100)]),
            discountPrice: new FormControl(null),
            transactionFee: new FormControl(null, [Validators.required]),
            count: new FormControl(null, [Validators.required]),
            isTopWeekly: new FormControl(false),
            isCarousel: new FormControl(false),
            maxUsePerUser: new FormControl(false),
            isAdultContent: new FormControl(false),
            expiryDate: new FormControl(null, [Validators.required]),
            redirectUrl: new FormControl(''),
            redeemType: new FormControl('', [Validators.required]),
        });
    }

    public ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.partnerId = Number(params['partnerId']);
            this.couponId = Number(params['id']);

            if (this.couponId) {
                this.handleSpinner(
                    firstValueFrom(this.vestraService.getCouponById(this.couponId))
                        .then(res => {
                            console.log(res)
                            if (res.isSuccess == false) {
                                this.router.navigate([`/administration/partner-list}`]);
                            }
                            this.item = res.result;
                            this.item.expiryDate = new Date(res.result.expiryDate);
                            this.init();
                            if(this.item.redeemType == 0)
                            {
                                this.form.get('redeemType').setValue("");
                            }
                            return Promise.resolve(res);
                        })
                        .catch((err: HttpErrorResponse) => {
                            this.validationHandlerService.handleServerError(err);
                        })
                );
            }
            this.handleSpinner(
                    firstValueFrom(this.vestraService.getCouponRedeemTypes())
                        .then(res => {
                            console.log(res)
                            this.couponRedeemTypes = res.result;
                            return Promise.resolve(res);
                        })
                        .catch((err: HttpErrorResponse) => {
                            this.validationHandlerService.handleServerError(err);
                        })
                );
        });
    }

    public ngAfterViewInit() {}

    public init() {
        this.form = this.getFormGroup();

        if (this.item) {
            this.form.patchValue(this.item);
            if (this.item.maxUsePerUser <= 0) {
                this.form.patchValue({
                    maxUsePerUser: true,
                });
            } else {
                this.form.patchValue({
                    maxUsePerUser: false,
                });
            }
            if (this.item.couponImageUrl) {
                this.setImgSrc('selected-couponImage', this.item.couponImageUrl);
            }
            if (this.item.couponGifUrl) {
                this.setImgSrc('selected-couponGif', this.item.couponGifUrl);
            }
        } else {
            this.clearSelectedImages();
            this.clearImageFileInput();
        }

        this.setDiscountControlsLogic();
    }

    public onDeleteConfirm = async () => {
        if (this.item.isDeleted) {
            this.openConfirmDialog(undefined, () => this.deleteItem(this.item.id), null, 'common.undelete_title', 'common.undelete_confirm');
        } else {
            this.openConfirmDialog(undefined, () => this.deleteItem(this.item.id));
        }
    };

    public closeEditPage(): void {
        this.router.navigate([`/administration/coupon-list/${this.partnerId}`]);
    }

    public deleteItem(itemIdToDeleteId: number): void {
        this.handleSpinner(
            firstValueFrom(this.vestraService.deleteOrUndeleteCoupon(itemIdToDeleteId))
                .then(res => {
                    this.router.navigate([`/administration/coupon-list/${this.partnerId}`]);
                    return Promise.resolve(res);
                })
                .catch((err: HttpErrorResponse) => {
                    this.validationHandlerService.handleServerError(err);
                })
        );
    }

    public onSave = async (): Promise<any> => {
        if (this.validationHandlerService.checkFormValid(this.form)) {
            if (!this.item && this.files['couponImage'] == null) {
                this.translatedToastrService.warning('common.image_is_required', 'common.warning');
                return;
            }
            let formValues = this.form.getRawValue();
            let coupon: CouponModel = <CouponModel>formValues;
            console.log(coupon);
            coupon.id = this.item?.id || 0;
            coupon.partnerId = this.partnerId;
            if (formValues.maxUsePerUser == true) {
                coupon.maxUsePerUser = 0;
            } else {
                coupon.maxUsePerUser = 1;
            }

            let itemToSave: CouponSaveModel = {
                coupon: coupon,
                couponImage: <any>this.files['couponImage'],
                couponGif: <any>this.files['couponGif'],
            };

            return this.handleSpinner(
                firstValueFrom(this.vestraService.updateCoupon(itemToSave))
                    .then(res => {
                        this.router.navigate([`/administration/coupon-list/${this.partnerId}`]);
                        return Promise.resolve(res);
                    })
                    .catch((error: any) => {
                        this.validationHandlerService.handleServerError(error, this.form);
                    })
            );
        }
    };

    public onFileSelected(event: any, type: 'couponImage' | 'couponGif'): void {
        const fileInput = event.target as HTMLInputElement;

        if (fileInput.files && fileInput.files.length > 0) {
            const file = fileInput.files[0];

            if (!this.isImageFileExtensionGood(file)) {
                this.onClearImage(type);
                this.translatedToastrService.warning('common.image_is_required', 'common.warning');
                return;
            }

            this.files[type] = file;
            this.fileNames[type] = file.name;

            const reader = new FileReader();
            reader.onloadend = () => {
                this.setImgSrc(`selected-${type}`, reader.result as any);
            };

            reader.readAsDataURL(file);
        }
    }

    public onClearImage(type: 'couponImage' | 'couponGif'): void {
        this.setEmptyFileInputValue(`${type}-file-input`);
        this.setEmptyImgSrc(`selected-${type}`);

        this.files[type] = null;
        this.fileNames[type] = '';
    }

    private clearSelectedImages() {
        this.setEmptyImgSrc('selected-couponImage');
        this.setEmptyImgSrc('selected-couponGif');
        this.files['couponImage'] = null;
        this.files['couponGif'] = null;
    }

    private clearImageFileInput() {
        this.setEmptyFileInputValue('couponImage-file-input');
        this.setEmptyFileInputValue('couponGif-file-input');
    }

    private setDiscountControlsLogic(): void {
        const discountPrice = this.form.get('discountPrice');
        const discountPercentage = this.form.get('discountPercentage');
        const estimatedPrice = this.form.get('estimatedPrice');

        discountPrice?.valueChanges.subscribe(value => {
            if (value) {
                discountPercentage?.setValue(null, { emitEvent: false });
                estimatedPrice?.setValue(value, { emitEvent: false });
                estimatedPrice?.disable({ emitEvent: false });
            } else {
                estimatedPrice?.enable({ emitEvent: false });
                estimatedPrice?.setValidators([Validators.required]);
                estimatedPrice?.updateValueAndValidity({ emitEvent: false });
            }
        });

        discountPercentage?.valueChanges.subscribe(value => {
            if (value) {
                discountPrice?.setValue(null, { emitEvent: false });
                estimatedPrice?.enable({ emitEvent: false });
                estimatedPrice?.setValidators([Validators.required]);
                estimatedPrice?.updateValueAndValidity({ emitEvent: false });
            }
        });
    }
}
